import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from 'src/app/services/communications/api.service';
import { UserDataService } from 'src/app/services/userData.service';
import { PhonePrefixService } from 'src/app/services/phone-prefix.service';
import { ValidatorsService } from '../../services/validators.service';
import { InvitationMode } from '../../constants/constants';
import { PhonePrefixOption } from '../../interfaces/PhonePrefixOption';
import { MatAutocompleteModule } from '@angular/material/autocomplete';


@Component({
  selector: 'app-send-invitation-link-modal',
  templateUrl: './send-invitation-link-modal.component.html',
  styleUrls: ['./send-invitation-link-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatSelectModule, MatAutocompleteModule ],
})

export class SendInvitationLinkModalComponent implements OnInit {
  @ViewChild('smsNumberRef') smsNumberRef!: NgModel;
  @ViewChild('smsPrefixRef') smsPrefixRef!: NgModel;
  @ViewChild('emailRef') emailRef!: NgModel;

  InvitationMode = InvitationMode;
  mode: InvitationMode = InvitationMode.Sms;
  smsPrefix: PhonePrefixOption | null = null;
  smsNumber: string = '';
  email: string = '';
  link: string = '';
  errorMessage: string = '';

  phonePrefixOptions: PhonePrefixOption[] = [];
  filteredPrefixes: PhonePrefixOption[] = [];

  constructor(public dialogRef: MatDialogRef<SendInvitationLinkModalComponent>, private apiService: ApiService, private translate: TranslateService, public userDataService: UserDataService, private phonePrefixService: PhonePrefixService,
    private validatorService: ValidatorsService) { }

  ngOnInit(): void {
    this.loadPhonePrefixes();
  }

  private loadPhonePrefixes(): void {
    this.phonePrefixService.getPhonePrefixOptions().subscribe({
      next: (options) => {
        this.phonePrefixOptions = options;
        this.filteredPrefixes = [...this.phonePrefixOptions];

        this.smsPrefixRef.control.valueChanges.subscribe(value => {
          const search = (value || '').toString().toLowerCase();
          this.filteredPrefixes = this.phonePrefixOptions.filter(p =>
            p.display.toLowerCase().includes(search) || p.dial_code.includes(search)
          );
        });
      },
      error: (error) => {
        console.error('Error loading phone prefixes:', error);
        this.phonePrefixOptions = [{ dial_code: '-1', display: '---' }];
      }
    });
  }

  setMode(newMode: InvitationMode): void { 
    this.mode = newMode;
    this.errorMessage = '';

    // Solo generar link cuando se selecciona el modo link y no hay link ya generado
    if (newMode === InvitationMode.Link && !this.link) {
      this.generateAnonymousLink();
    }
  }

  sendSMS(): void {
    var error = false;
    const prefixControl = this.smsPrefixRef.control;
    const phoneControl = this.smsNumberRef.control;

    prefixControl.setErrors(null);
    phoneControl.setErrors(null);

    const prefix = prefixControl.value != null ? prefixControl.value.dial_code : '';
    const phone = phoneControl.value || '';

    var phoneNumber = prefix + phone;

    if (phone == '' && (!prefix || prefix === '' || prefix === '-1')) {
      prefixControl.setErrors({ phonePrefixRequired: true });
      phoneControl.setErrors({ phoneInvalid: true });
      prefixControl.markAsTouched();
      phoneControl.markAsTouched();
      error = true;
    }

    if (phone != '' && (!prefix || prefix === '' || prefix === '-1')) {
      prefixControl.setErrors({ phonePrefixRequired: true });
      prefixControl.markAsTouched();
      error = true;
    }

    if (prefix != '' && prefix != '-1' && !this.validatorService.isValidPhone(phoneNumber)) {
      phoneControl.setErrors({ phoneInvalid: true });
      phoneControl.markAsTouched();
      error = true;
    } 

    if (error) return;

    this.sendInvitation(phoneNumber, false);
  }

  sendEmail(): void {
    const control = this.emailRef.control;
    control.setErrors(null);

    var email = this.email.trim();

    if (!this.validatorService.isValidEmail(email)) {
      control.setErrors({ email: true });
      control.markAsTouched();
      return;
    }

    this.sendInvitation(email, true);
  }

  copyLink(): void {
    this.errorMessage = '';

    if (!this.link) {
      this.errorMessage = this.translate.instant('LINK_DIALOG.NO_LINK_AVAILABLE') + ". " + this.translate.instant("TRY_LATER");
      return;
    }

    navigator.clipboard.writeText(this.link).then(() => {
      this.dialogRef.close({
        success: true,
        action:'link_copied'
      });
    }).catch(err => {
      console.error('Error copying link:', err);
      this.setErrorMessage('LINK_DIALOG.COPY_ERROR');
    });
  }

  clearError(): void {
    this.errorMessage = '';
  }

  private setErrorMessage(translationKey: string): void {
    this.translate.get(translationKey).subscribe((text: string) => {
      this.errorMessage = text;
    });
  }

  isSMSButtonDisabled(): boolean {
    return this.smsPrefix?.dial_code === '-1' || !this.smsNumber || !this.smsNumber.trim();
  }

  isEmailButtonDisabled(): boolean {
    return !this.email || !this.email.trim();
  }

  shouldShowError(): boolean {
    return !!this.errorMessage;
  }

  private sendInvitation(inputData: string, isEmail: boolean): void {
    this.apiService.sendInvitation(inputData, isEmail, '', this.userDataService.AssetId!, null, null, this.userDataService.AssistanceToOpen!).subscribe({
      next: (response) => {
        
        this.dialogRef.close({
          success: true,
          type: 'sendInvitation',
          email: isEmail ? this.email : null,
          response: response,
          phonePrefix: !isEmail ? this.smsPrefix?.dial_code : null,
          phoneNumber: !isEmail ? this.smsNumber : null
        });
      },
      error: (error) => {
        console.error('Error sending invitation:', error);

        this.dialogRef.close({
          success: false,
        });

      }
    });
  }

  private generateAnonymousLink(): void {
    if (!this.userDataService) return;

    this.errorMessage = '';

    this.apiService.generateAnonymousLink(this.userDataService.AssetId!, this.userDataService.AssistanceToOpen)
      .subscribe({
        next: (response: any) => {
          this.link = response.Link;
        },
        error: (error: any) => {
          console.error('Error en generateAnonymousLink:', error.error.Error);

          this.translate.get('LINK_DIALOG.DIRECT_CALL_ERROR').subscribe((text: string) => {
            this.errorMessage = text;
            if (error.error.Error != '') this.errorMessage += ": " + error.error.Error;  
          });

        }
      });
  }

  displayPrefix(prefix: PhonePrefixOption): string {
    if (!prefix) return '';
    return prefix ? `${prefix.display}` : '';
  }

  onAutocompleteOpened() {
    this.filteredPrefixes = [...this.phonePrefixOptions];
    setTimeout(() => {
      const selected = document.querySelector('.mat-mdc-option[aria-selected="true"]');
      selected?.scrollIntoView({ block: 'center' });
    }, 0);
  }

}
